// ============================================================================
// OSINT Confidence Scoring Engine — Sprint 006 Feature 003
// ============================================================================
// 3 signal weights (configurable per spec):
//   - name_match    (0.35) — fuzzy compare nama subjek vs profile display name
//   - photo_match   (0.45) — InsightFace cosine similarity (reuse Feature 001)
//   - username_match (0.20) — operator hint vs profile username
//
// Final confidence band:
//   ≥ 0.85 → High match
//   0.65–0.84 → Likely
//   0.50–0.64 → Possible
//   < 0.50 → Hidden (configurable display)
// ============================================================================

import { getAuthHeaders } from '../auth';

const W_NAME     = 0.35;
const W_PHOTO    = 0.45;
const W_USERNAME = 0.20;

/**
 * Levenshtein distance — untuk fuzzy name matching.
 * Returns 0..1 similarity (1 = exact, 0 = totally different).
 */
function nameSimilarity(a, b) {
  if (!a || !b) return 0;
  const s1 = String(a).toLowerCase().trim();
  const s2 = String(b).toLowerCase().trim();
  if (s1 === s2) return 1;
  if (s1.length === 0 || s2.length === 0) return 0;

  // Exact substring (e.g. "Andra Sandi" includes "Andra")
  if (s1.includes(s2) || s2.includes(s1)) {
    const ratio = Math.min(s1.length, s2.length) / Math.max(s1.length, s2.length);
    return 0.6 + ratio * 0.4; // partial substring → 0.6-1.0
  }

  // Token-based: berapa banyak kata yang match
  const t1 = new Set(s1.split(/\s+/).filter(t => t.length >= 2));
  const t2 = new Set(s2.split(/\s+/).filter(t => t.length >= 2));
  if (t1.size === 0 || t2.size === 0) return 0;
  let matches = 0;
  for (const tok of t1) if (t2.has(tok)) matches++;
  const tokenScore = matches / Math.max(t1.size, t2.size);

  // Levenshtein full
  const m = s1.length, n = s2.length;
  const dp = Array.from({ length: m + 1 }, () => new Array(n + 1).fill(0));
  for (let i = 0; i <= m; i++) dp[i][0] = i;
  for (let j = 0; j <= n; j++) dp[0][j] = j;
  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      dp[i][j] = s1[i - 1] === s2[j - 1]
        ? dp[i - 1][j - 1]
        : 1 + Math.min(dp[i - 1][j], dp[i][j - 1], dp[i - 1][j - 1]);
    }
  }
  const distScore = 1 - dp[m][n] / Math.max(m, n);

  // Ambil yang lebih besar antara token-based & levenshtein
  return Math.max(tokenScore, distScore);
}

/**
 * Username similarity — variant lebih sensitif untuk handle.
 * "andra_sandi" vs "andrasandi" → high match.
 */
function usernameSimilarity(a, b) {
  if (!a || !b) return 0;
  const norm = (s) => String(s).toLowerCase().replace(/[._\-\s]/g, '');
  return nameSimilarity(norm(a), norm(b));
}

/**
 * Photo match — REAL via /api/face-compare (reuse Feature 001 InsightFace).
 * Per spec § 4.4 signal #2 + § 4.4a Photo Collection Scope:
 *   - Compare subject photo (KTP/Datacenter) vs ALL candidate photos
 *   - Best matches across photo set (cap 20 photos)
 *   - Threshold similarity ≥ 0.85 = match
 *   - Output: best_similarity 0-1 (ke confidence score) + match_count
 *
 * Untuk Stage 1 discovery cuma punya 1 thumbnail per kandidat — jadi kirim 1.
 * Stage 2 enrichment yang baru collect banyak photos → bisa kirim semua.
 */
async function photoSimilarity(subject, candidateThumbnailUrl, additionalPhotos = []) {
  // Backward compat: kalau subject masih dilempar sebagai URL string
  const subjectPhotoUrl  = typeof subject === 'string' ? subject : subject?.foto_url;
  const subjectEmbedding = typeof subject === 'object' && Array.isArray(subject?.face_embedding)
    ? subject.face_embedding
    : null;

  if (!subjectPhotoUrl && !subjectEmbedding) {
    return { score: 0, match_count: 0, total_count: 0, explanation: '' };
  }
  if (!candidateThumbnailUrl) {
    return { score: 0, match_count: 0, total_count: 0, explanation: '' };
  }

  const allCandidatePhotos = [candidateThumbnailUrl, ...additionalPhotos].filter(Boolean);

  try {
    // Pakai pre-computed embedding kalau ada di Supabase (hemat 1-2 detik per call)
    const body = subjectEmbedding
      ? { subject_embedding: subjectEmbedding, candidate_photos: allCandidatePhotos }
      : { subject_photo: subjectPhotoUrl,     candidate_photos: allCandidatePhotos };

    const res = await fetch('/api/face-compare', {
      method:  'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
      body:    JSON.stringify(body),
      signal:  AbortSignal.timeout(45000),
    });

    if (!res.ok) {
      console.warn('[osint/scoring] face-compare returned', res.status);
      return { score: 0, match_count: 0, total_count: allCandidatePhotos.length, explanation: '' };
    }

    const data = await res.json();
    return {
      score:       data.best_similarity || 0,
      match_count: data.match_count || 0,
      total_count: data.total_count || allCandidatePhotos.length,
      explanation: data.explanation || '',
    };
  } catch (err) {
    console.warn('[osint/scoring] face-compare error:', err.message);
    return { score: 0, match_count: 0, total_count: 0, explanation: '' };
  }
}

/**
 * Score 1 candidate. Returns { confidence, match_signals }.
 *
 * @param {object} subject    - { nama_lengkap, foto_url, username? }
 * @param {object} candidate  - { display_name, thumbnail_url, handle, raw }
 *
 * Weighting strategy:
 *   - Bobot di-redistribute hanya ke signal yang AVAILABLE (jangan penalize
 *     kandidat platform yang tidak expose foto, mis. Threads/YouTube).
 *   - Username override: saat operator EXPLISIT masukkan username hint dan
 *     match ≥ 0.85 ke handle kandidat, username jadi signal dominan
 *     (intent operator override weak photo/name).
 *   - Floor: usernameScore ≥ 0.95 (exact match) → confidence minimum 0.85
 *     (operator deliberate input deserves High tier).
 */
export async function scoreCandidate(subject, candidate) {
  const candidateName = candidate.display_name || candidate.handle;
  const nameScore     = nameSimilarity(subject.nama_lengkap, candidateName);
  // Pass full subject object — photoSimilarity pakai face_embedding (Supabase)
  // kalau available, fallback ke foto_url. Embedding pre-computed jauh lebih
  // cepat (skip download + extract subject) dan canonical (hasil enrollment KTP).
  const photoResult   = await photoSimilarity(subject, candidate.thumbnail_url);
  const usernameScore = subject.username
    ? usernameSimilarity(subject.username, candidate.handle)
    : 0;

  // Detect available signals — only count weight for signals yang punya data.
  const hasName     = !!(subject.nama_lengkap && candidateName);
  const hasPhoto    = !!(subject.foto_url && candidate.thumbnail_url && photoResult.total_count > 0);
  const hasUsername = !!subject.username;

  // Base raw weights per signal availability
  let wName     = hasName     ? W_NAME     : 0;
  let wPhoto    = hasPhoto    ? W_PHOTO    : 0;
  let wUsername = hasUsername ? W_USERNAME : 0;

  // Username override — operator explicitly target username + strong match.
  // Username jadi dominant signal (60%), name & photo (kalau ada) jadi sekunder.
  const usernameOverride = hasUsername && usernameScore >= 0.85;
  if (usernameOverride) {
    wUsername = 0.60;
    wName     = hasName  ? 0.25 : 0;
    wPhoto    = hasPhoto ? 0.15 : 0;
  }

  // Normalize weights → sum = 1 (no penalty for missing signals).
  const totalWeight = wName + wPhoto + wUsername;
  if (totalWeight > 0) {
    wName     /= totalWeight;
    wPhoto    /= totalWeight;
    wUsername /= totalWeight;
  }

  let confidence = nameScore * wName + photoResult.score * wPhoto + usernameScore * wUsername;

  // Floor confidence — biometric & deliberate signals deserve High tier minimum.
  //
  // Tanpa username hint, signal kuat satu-satunya = foto (biometric ArcFace
  // 512-dim cosine). Threshold:
  //   ≥ 0.85 cosine = biometric strong match → confidence floor 0.85 (High)
  //   ≥ 0.65 cosine = biometric likely match → confidence floor 0.70 (Likely)
  //
  // Plus name-only floor — saat operator search by nama saja (no username,
  // no photo, mis. court records / news mention), exact name match deserves
  // recognition.
  if (hasUsername && usernameScore >= 0.95) {
    confidence = Math.max(confidence, 0.85);
  } else if (hasUsername && usernameScore >= 0.85) {
    confidence = Math.max(confidence, 0.70);
  }
  // Threshold cross-domain (KTP vs sosmed) — 0.80 strong, 0.60 valid match.
  if (hasPhoto && photoResult.score >= 0.80) {
    confidence = Math.max(confidence, 0.85);
  } else if (hasPhoto && photoResult.score >= 0.60) {
    confidence = Math.max(confidence, 0.70);
  } else if (hasPhoto && photoResult.score >= 0.45) {
    confidence = Math.max(confidence, 0.55);
  }
  // Name-only fallback: kalau operator cuma kasih nama (no username + no photo
  // available), exact full-name match → minimum Likely tier.
  if (!hasUsername && !hasPhoto && hasName && nameScore >= 0.95) {
    confidence = Math.max(confidence, 0.70);
  }

  confidence = Math.min(1, Math.max(0, confidence));

  const weights = { name: Number(wName.toFixed(3)), photo: Number(wPhoto.toFixed(3)), username: Number(wUsername.toFixed(3)) };

  // Per spec § 4.4 item 15: explanation harus reference 3 signals + photo count
  const nameExplain     = nameScore >= 0.95 ? 'Nama: exact match' : nameScore >= 0.7 ? `Nama: ${Math.round(nameScore * 100)}% match` : '';
  const photoExplain    = photoResult.match_count > 0
                            ? `Foto: ${photoResult.match_count} dari ${photoResult.total_count} foto match`
                            : (!hasPhoto && hasUsername ? 'Foto: tidak tersedia' : '');
  const usernameExplain = usernameScore >= 0.95 ? 'Username: exact match'
                        : usernameScore >= 0.7  ? `Username: ${Math.round(usernameScore * 100)}% match`
                        : '';
  const signalExplain   = [nameExplain, photoExplain, usernameExplain].filter(Boolean).join(' · ');

  return {
    confidence: Number(confidence.toFixed(3)),
    match_signals: {
      name_match:        Number(nameScore.toFixed(3)),
      photo_match:       Number(photoResult.score.toFixed(3)),
      photo_match_count: photoResult.match_count,
      photo_total_count: photoResult.total_count,
      username_match:    Number(usernameScore.toFixed(3)),
      username_override: usernameOverride,
      weights,
    },
    signal_explain: signalExplain || 'No strong match signals',
  };
}

export { nameSimilarity, usernameSimilarity, photoSimilarity };
